import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { ProtectedRoute } from '../auth/ProtectedRoute';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Modal } from '../components/common/Modal';
import { adminApi } from '../api/admin';
import type { ModeratorResponse, AdministratorInfo } from '../types/admin';
import { useLanguage } from '../i18n/useLanguage';
import { hasAnyRole } from '../auth/roleUtils';

const AdminModeratorsPageContent: React.FC = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [moderators, setModerators] = useState<ModeratorResponse[]>([]);
  const [administrators, setAdministrators] = useState<AdministratorInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [adminsLoading, setAdminsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addUsername, setAddUsername] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addAdminUsername, setAddAdminUsername] = useState('');
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [addAdminError, setAddAdminError] = useState<string | null>(null);
  const [removeUserId, setRemoveUserId] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const isAdmin = hasAnyRole(user?.roles, 'ADMIN');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    const fetchModerators = async () => {
      setLoading(true);
      setError(null);
      try {
        const list = await adminApi.getModerators();
        setModerators(list);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load moderators');
      } finally {
        setLoading(false);
      }
    };
    const fetchAdministrators = async () => {
      setAdminsLoading(true);
      try {
        const list = await adminApi.getAdministrators();
        setAdministrators(list);
      } catch {
        // non-critical — silently ignore
      } finally {
        setAdminsLoading(false);
      }
    };
    fetchModerators();
    fetchAdministrators();
  }, [isAdmin, navigate]);

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    const username = addAdminUsername.trim();
    if (!username) {
      setAddAdminError('Enter a username');
      return;
    }
    setAddingAdmin(true);
    setAddAdminError(null);
    try {
      await adminApi.addAdministrator({ username });
      setAddAdminUsername('');
      const list = await adminApi.getAdministrators();
      setAdministrators(list);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      const message = ax?.response?.data?.message ?? (err instanceof Error ? err.message : 'Failed to add administrator');
      setAddAdminError(message);
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const username = addUsername.trim();
    if (!username) {
      setAddError('Enter a username');
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      await adminApi.addModerator({ username });
      setAddUsername('');
      const list = await adminApi.getModerators();
      setModerators(list);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      const message = ax?.response?.data?.message ?? (err instanceof Error ? err.message : 'Failed to add moderator');
      setAddError(message);
    } finally {
      setAdding(false);
    }
  };

  const confirmRemove = async () => {
    if (!removeUserId) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await adminApi.removeModerator(removeUserId);
      setModerators(prev => prev.filter(m => m.userId !== removeUserId));
      setRemoveUserId(null);
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Failed to remove moderator');
    } finally {
      setRemoving(false);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(language === 'de' ? 'de-DE' : 'en-US');
    } catch {
      return iso;
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="page page--narrow">
      <h1 className="page-title">Manage moderators</h1>
      <p className="page-description">
        Add or remove the MODERATOR role from users. Only users who have posted, commented, or messaged can be found by username.
      </p>

      {error && (
        <ErrorMessage message={error} onDismiss={() => setError(null)} />
      )}

      <section className="admin-administrators-list" style={{ marginBottom: 'var(--spacing-xl)' }}>
        <h2 className="section-title" style={{ marginBottom: 'var(--spacing-sm)' }}>Administrators</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9em', marginBottom: 'var(--spacing-sm)' }}>
          Users with the ADMIN role in Keycloak.
        </p>
        {adminsLoading ? (
          <LoadingSpinner />
        ) : administrators.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>No administrators found.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {administrators.map((a) => (
              <li
                key={a.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  borderBottom: '1px solid var(--border-color)',
                  gap: 'var(--spacing-md)',
                }}
              >
                <strong>{a.username}</strong>
                {(a.firstName || a.lastName) && (
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.9em' }}>
                    {[a.firstName, a.lastName].filter(Boolean).join(' ')}
                  </span>
                )}
                {a.email && (
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85em' }}>
                    {a.email}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="admin-administrators-add" style={{ marginBottom: 'var(--spacing-xl)' }}>
        <h2 className="section-title" style={{ marginBottom: 'var(--spacing-sm)' }}>Add administrator</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9em', marginBottom: 'var(--spacing-sm)' }}>
          Assign the ADMIN role to a user. Only users who have posted, commented, or messaged can be found by username.
        </p>
        <form onSubmit={handleAddAdmin} style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Input
            id="add-admin-username"
            type="text"
            label="Username"
            value={addAdminUsername}
            onChange={(e) => setAddAdminUsername(e.target.value)}
            placeholder="e.g. johndoe"
            disabled={addingAdmin}
            style={{ minWidth: 200 }}
          />
          <Button type="submit" disabled={addingAdmin} isLoading={addingAdmin}>
            Add as administrator
          </Button>
        </form>
        {addAdminError && (
          <ErrorMessage message={addAdminError} onDismiss={() => setAddAdminError(null)} className="admin-administrators-add-error" />
        )}
      </section>

      <section className="admin-moderators-add" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <h2 className="section-title" style={{ marginBottom: 'var(--spacing-sm)' }}>Add moderator</h2>
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Input
            id="add-username"
            type="text"
            label="Username"
            value={addUsername}
            onChange={(e) => setAddUsername(e.target.value)}
            placeholder="e.g. johndoe"
            disabled={adding}
            style={{ minWidth: 200 }}
          />
          <Button type="submit" disabled={adding} isLoading={adding}>
            Add as moderator
          </Button>
        </form>
        {addError && (
          <ErrorMessage message={addError} onDismiss={() => setAddError(null)} className="admin-moderators-add-error" />
        )}
      </section>

      <section className="admin-moderators-list">
        <h2 className="section-title" style={{ marginBottom: 'var(--spacing-sm)' }}>Current moderators</h2>
        {loading ? (
          <LoadingSpinner />
        ) : moderators.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>No moderators in the list yet.</p>
        ) : (
          <ul className="admin-moderators-list-items" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {moderators.map((m) => (
              <li
                key={m.userId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  borderBottom: '1px solid var(--border-color)',
                  gap: 'var(--spacing-md)',
                }}
              >
                <div>
                  <strong>{m.username}</strong>
                  <span style={{ color: 'var(--text-muted)', marginLeft: 'var(--spacing-sm)', fontSize: '0.9em' }}>
                    Added {formatDate(m.addedAt)}
                  </span>
                </div>
                <Button
                  variant="danger"
                  size="small"
                  onClick={() => { setRemoveUserId(m.userId); setRemoveError(null); }}
                  disabled={removing}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Modal
        isOpen={removeUserId !== null}
        onClose={() => !removing && setRemoveUserId(null)}
        title="Remove moderator"
      >
        <p>Remove MODERATOR role from this user? They will lose moderator capabilities.</p>
        {removeError && (
          <ErrorMessage message={removeError} onDismiss={() => setRemoveError(null)} />
        )}
        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'flex-end', marginTop: 'var(--spacing-md)' }}>
          <Button variant="outline" onClick={() => setRemoveUserId(null)} disabled={removing}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmRemove} isLoading={removing} disabled={removing}>
            Remove
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export const AdminModeratorsPage: React.FC = () => (
  <ProtectedRoute>
    <AdminModeratorsPageContent />
  </ProtectedRoute>
);
